import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link, useHistory, useLocation } from "react-router-dom";
import { TbChartBar, TbPlus } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { fetchBaasStablesTransactions } from "../../../../Services/baasStablesApi";
import {
  fetchBaasStablesWallet,
  fetchBaasStablesWalletEntities,
  fetchBaasStablesWalletsList,
  fetchBaasPeerStablesWallets,
  getBaasStablesWalletListAssets,
} from "../../../../Services/baasStablesWalletApi";
import { BaasStablesWalletPageSkeleton } from "../components/BaasSkeleton";
import { BAAS_STABLES_METRICS_PATH, BAAS_STABLES_WALLET_PATH, buildBaasAccountLimitesPath } from "../constants";
import {
  EMPTY_BAAS_STABLES_WALLET_LIST_FILTERS,
  filterBaasStablesWallets,
} from "./baasStablesWalletFilters";
import { BaasStablesBalanceCards } from "./components/BaasStablesBalanceCards";
import { BaasStablesLimitsBar } from "./components/BaasStablesLimitsBar";
import { BaasStablesMetricsPanel } from "./components/BaasStablesMetricsPanel";
import { BaasStablesRecentActivity } from "./components/BaasStablesRecentActivity";
import { BaasStablesWalletCreateModal } from "./components/BaasStablesWalletCreateModal";
import { BaasStablesWalletQuickActions } from "./components/BaasStablesWalletQuickActions";
import { BaasStablesWalletsFilterBar } from "./components/BaasStablesWalletsFilterBar";
import { BaasStablesWalletsTable } from "./components/BaasStablesWalletsTable";
import {
  canUseBaasGlobalInstanceFilter,
  resolveBaasListInstanceNumericId,
  rowMatchesBaasListInstanceFilter,
  shouldFetchBaasPeerLists,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";

const getBlockMessage = (blockReason) => {
  if (blockReason === "product_disabled") {
    return intl.get("baas.home.capabilities.productDisabled");
  }

  if (blockReason === "permission_denied") {
    return intl.get("baas.home.capabilities.permissionDenied");
  }

  return intl.get("baas.stablesWallet.capabilityBlocked");
};

const shouldOpenCreate = (search) => new URLSearchParams(search).get("create") === "1";

export const BaasStablesWalletPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { selectedInstance, instances, listInstanceFilter } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can, getBlockReason } = useBaasCapabilities();

  const capabilityId = "processing.stables.view";
  const operateCapabilityId = "stables.operate";
  const createCapabilityId = "stables.wallets.create";

  const allowed = can(capabilityId);
  const canOperate = can(operateCapabilityId);
  const canCreate = can(createCapabilityId);

  const [createOpen, setCreateOpen] = useState(false);

  const [entities, setEntities] = useState([]);
  const [walletsList, setWalletsList] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [walletListFilters, setWalletListFilters] = useState(EMPTY_BAAS_STABLES_WALLET_LIST_FILTERS);
  const [reloadToken, setReloadToken] = useState(0);
  const hasLoadedOnce = walletsList.length > 0 || Boolean(wallet) || loadError;

  // Soft-redirect legacy `?section=metrics` tab to dedicated metrics page.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("section") === "metrics") {
      history.replace(BAAS_STABLES_METRICS_PATH);
    }
  }, [history, location.search]);

  useEffect(() => {
    if (shouldOpenCreate(location.search) && canCreate) {
      setCreateOpen(true);
    }
  }, [canCreate, location.search]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setEntities([]);
        setWalletsList([]);
        setWallet(null);
        setTransactions([]);
        setLoadError(false);
        return;
      }

      setDataLoading(true);
      setLoadError(false);
      try {
        const [nextEntities, nextWallets, nextTx, peerWallets] = await Promise.all([
          fetchBaasStablesWalletEntities(selectedInstance),
          fetchBaasStablesWalletsList(selectedInstance),
          fetchBaasStablesTransactions(selectedInstance).catch(() => []),
          shouldFetchBaasPeerLists(selectedInstance, listInstanceFilter)
            ? fetchBaasPeerStablesWallets(selectedInstance, {
                instanceId: resolveBaasListInstanceNumericId(
                  listInstanceFilter,
                  selectedInstance,
                  instances,
                ),
              })
            : Promise.resolve([]),
        ]);
        // Instance-wide aggregate for saldos/limites/atividade — not first entity only
        // (first entity can be empty while another holds USDT from compra).
        const nextWallet = await fetchBaasStablesWallet(selectedInstance, null);
        if (!cancelled) {
          setEntities(nextEntities);
          const byId = new Map();
          for (const row of nextWallets) {
            if (!row?.id) continue;
            byId.set(row.id, stampBaasRowInstance(row, selectedInstance));
          }
          for (const row of peerWallets) {
            if (!row?.id) continue;
            byId.set(row.id, row);
          }
          setWalletsList([...byId.values()]);
          setWallet(nextWallet);
          setTransactions(nextTx);
          setLoadError(false);
        }
      } catch {
        if (!cancelled) {
          setEntities([]);
          setWalletsList([]);
          setWallet(null);
          setTransactions([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, reloadToken, listInstanceFilter, instances]);

  const walletListAssets = useMemo(
    () => getBaasStablesWalletListAssets(walletsList),
    [walletsList],
  );

  const showInstanceColumn = canUseBaasGlobalInstanceFilter(selectedInstance);

  const filteredWallets = useMemo(
    () =>
      filterBaasStablesWallets(walletsList, walletListFilters).filter((row) =>
        rowMatchesBaasListInstanceFilter(
          row,
          listInstanceFilter,
          selectedInstance,
          instances,
        ),
      ),
    [walletsList, walletListFilters, listInstanceFilter, selectedInstance, instances],
  );

  const handleWalletListFiltersChange = (nextFilters) => {
    setWalletListFilters(nextFilters);
  };

  const openCreate = () => {
    setCreateOpen(true);
    const params = new URLSearchParams(location.search);
    params.set("create", "1");
    params.delete("section");
    history.replace(`${BAAS_STABLES_WALLET_PATH}?${params.toString()}`);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    const params = new URLSearchParams(location.search);
    params.delete("create");
    const query = params.toString();
    history.replace(query ? `${BAAS_STABLES_WALLET_PATH}?${query}` : BAAS_STABLES_WALLET_PATH);
  };

  const handleCreated = (createdWallet) => {
    // Optimistic insert (Pix keys pattern) so empty → first key doesn't flash empty again
    // if list fetch lags; then soft-reload to reconcile with the API.
    if (createdWallet?.id) {
      setWalletsList((prev) => {
        if (prev.some((item) => item.id === createdWallet.id)) return prev;
        return [createdWallet, ...prev];
      });
    }
    setReloadToken((token) => token + 1);
  };

  // Soft refresh after create: keep the page mounted (and modal close) instead of
  // swapping to a full-page skeleton whenever wallets already exist.
  if (isLoading || (dataLoading && !hasLoadedOnce)) {
    return <BaasStablesWalletPageSkeleton />;
  }

  const pageHeader = (
    <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.nav.stablesWallet")}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.stablesWallet.subtitle")}
        </p>
      </div>
      {allowed ? (
        <div className="flex flex-wrap items-center gap-2">
          <Link to={BAAS_STABLES_METRICS_PATH} className="btn-outline shrink-0">
            <TbChartBar className="h-4 w-4" aria-hidden />
            {intl.get("baas.stablesWallet.actions.viewMetrics")}
          </Link>
          {canCreate ? (
            <button
              type="button"
              className="btn-primary shrink-0"
              onClick={openCreate}
            >
              <TbPlus className="h-4 w-4" aria-hidden />
              {intl.get("baas.stablesWallet.actions.create")}
            </button>
          ) : null}
        </div>
      ) : null}
    </header>
  );

  if (!allowed) {
    return (
      <div className="baas-stables-wallet min-w-0 space-y-5">
        {pageHeader}
        <div className="app-card app-empty-state">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {getBlockMessage(getBlockReason(capabilityId))}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="baas-stables-wallet min-w-0 space-y-5">
      {pageHeader}

      {loadError ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {intl.get("baas.stablesWallet.loadFailed")}
          </p>
          <button
            type="button"
            className="btn-outline mt-4"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            {intl.get("baas.stablesWallet.retry")}
          </button>
        </div>
      ) : null}

      {!loadError && walletsList.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-brand-dark-border dark:bg-brand-dark-elevated/30">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            {intl.get("baas.stablesWallet.empty")}
          </p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.stablesWallet.emptyHint")}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <Link to={BAAS_STABLES_METRICS_PATH} className="btn-outline">
              <TbChartBar className="h-4 w-4" aria-hidden />
              {intl.get("baas.stablesWallet.actions.viewMetrics")}
            </Link>
            {canCreate ? (
              <button
                type="button"
                className="btn-primary"
                onClick={openCreate}
              >
                <TbPlus className="h-4 w-4" aria-hidden />
                {intl.get("baas.stablesWallet.actions.create")}
              </button>
            ) : null}
          </div>
          {!canCreate ? (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.create.permissionHint")}
            </p>
          ) : null}
        </div>
      ) : null}

      {!loadError && walletsList.length > 0 ? (
        <>
          <BaasStablesWalletQuickActions disabled={!canOperate} />

          {wallet ? (
            <BaasStablesBalanceCards balances={wallet.balances} iconUrls={wallet.iconUrls} />
          ) : null}

          {wallet ? (
            <BaasStablesLimitsBar
              limits={wallet.limits}
              configureTo={
                canOperate && wallet.accountId
                  ? buildBaasAccountLimitesPath(wallet.accountId, "stables")
                  : null
              }
            />
          ) : null}

          <div className="space-y-3">
            <BaasStablesMetricsPanel wallets={walletsList} transactions={transactions} compact />
            <div className="flex justify-end">
              <Link
                to={BAAS_STABLES_METRICS_PATH}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#c4892a] transition hover:text-[#e7a73f] dark:text-[#e7a73f]"
              >
                <TbChartBar className="h-3.5 w-3.5" aria-hidden />
                {intl.get("baas.stablesWallet.metrics.openFull")}
              </Link>
            </div>
          </div>

          {wallet ? (
            <BaasStablesRecentActivity operations={wallet.recentOperations} />
          ) : null}

          <BaasStablesWalletsFilterBar
            entities={entities}
            assets={walletListAssets}
            filters={walletListFilters}
            onFiltersChange={handleWalletListFiltersChange}
            filteredItems={filteredWallets}
          />

          <BaasStablesWalletsTable
            wallets={filteredWallets}
            onCreate={canCreate ? openCreate : undefined}
            showInstanceColumn={showInstanceColumn}
          />
        </>
      ) : null}

      {canCreate ? (
        <BaasStablesWalletCreateModal
          open={createOpen}
          wallets={walletsList}
          onClose={closeCreate}
          onCreated={handleCreated}
        />
      ) : null}
    </div>
  );
};
